import { subscriptionToUpdate } from 'src/services/app-sync';
import { DocumentNode } from 'graphql';
import apolloStore from 'src/stores/apolloStore';
import authStore from 'src/stores/authStore';
import gql, { MobxApolloQuery } from 'src/shared/mobx-apollo';
import {
    Identifiable,
    processPotentialArray,
    Reducable,
    PotentialArray,
    ListResult,
    QueryListResult,
    queryListResultConverter,
    getFirstKey,
    reduce,
    DataClass,
    processArrayAsync
} from 'src/shared/utility';
import { observable, computed, action, autorun, reaction } from 'mobx';
import { ApolloClient, MutationOptions, SubscribeToMoreOptions, FetchPolicy } from 'apollo-client';
import { ClientDeleteInput, UnitDeleteInput } from 'src/services/graphql/Models';
import { removeTypenameRecursive } from 'src/shared/typemap';

type DeleteInput = ClientDeleteInput | UnitDeleteInput;
interface CacheOperation <T extends Identifiable, C extends DataClass> {
    (prevData: ListResult<T>, newData: T): void;
}

// type MutationType = 'CREATED' | 'UPDATED' | 'DELETED';
interface LastGQL<T extends Identifiable> {
    hash: string;
    gqlObject: MobxApolloQuery<QueryListResult<T>>;
    variables: any;
}

export default abstract class GenericDataStorage<T extends Identifiable, O extends DataClass> {
    manualLoad: boolean = true;
    operationErrors: any;

    @observable isLoadingMore: PotentialArray <boolean>;
    @observable _lastQuery?: PotentialArray< LastGQL<T> >;
    @observable lastUpdate?: number;

    abstract listQueries: DocumentNode;
    abstract listVariables: Reducable< PotentialArray< any > >;
    abstract _createQueries?: Reducable< DocumentNode >;
    abstract _updateQueries?: Reducable< DocumentNode >;
    abstract _deleteQueries?: Reducable< DocumentNode >;
    _allSubscriptions: Reducable< SubscribeToMoreOptions [] > = [];

    abstract parseDelete(obj: O): DeleteInput;
    abstract toObject(from: T, index?: number, array?: T[]): O;
    abstract countError(obj: T): any[];
    
    get createQueries () {
        return reduce(this._createQueries);
    }
    get updateQueries () {
        return reduce(this._updateQueries);
    }

    get deleteQueries () {
        return reduce(this._deleteQueries);
    }

    constructor(manualLoad?: boolean) {
        this.manualLoad = manualLoad ? true : false;
        autorun(this.loadCall.bind(this));
        
        const me = this;
        const updateQuery = this.updateQuery.bind(this);

        reaction(() => me.listVariables, updateQuery);
        reaction(() => authStore.clientId, updateQuery);
        reaction(() => apolloStore.client, updateQuery);
    }

    @computed get canCreate(): boolean {
        if (typeof this.createQueries !== 'undefined') {
            return true;
        } else {
            return false;
        }

    }

    @computed get canUpdate(): boolean {
        if (typeof this.updateQueries !== 'undefined') {
            return true;
        } else {
            return false;
        }

    }

    @computed get canDelete(): boolean {
        if (typeof this.deleteQueries !== 'undefined') {
            return true;
        } else {
            return false;
        }

    }

    @computed.struct get queryListResult(): PotentialArray < MobxApolloQuery<QueryListResult<T>> | undefined > {
        if ( 
            apolloStore.isProvided && 
            authStore.clientId && 
            authStore.clientId.length > 0 &&
            this._lastQuery
        ) {
            let ret = processPotentialArray(this._lastQuery, (input) => {
                if (input.gqlObject) {
                    return input.gqlObject;
                } else {
                    return undefined;
                }
            });

            if (
                Array.isArray(ret) &&
                ret.every(o => typeof o === 'undefined')
            ) {
                return undefined;
            }

            return ret;
        } else {
            return undefined;
        }
    }

    @action updateQuery(index?: number) {
        if ( 
            apolloStore.isProvided && 
            authStore.clientId && 
            authStore.clientId.length > 0
        ) {
            const me = this;
            let variables = reduce(this.listVariables);
            if (isNaN(index)) {
                processPotentialArray(
                    variables, 
                    (ip, id) => me.getQuery(ip, id)
                );
            } else {
                processPotentialArray(
                    variables, 
                    (ip, id) => me.getQuery(ip, id),
                    index
                );
            }
        }
    }
    onFetch(input: any) {
        // not yet implemented
        this.forceUpdate();
    }

    @action onError(input: any) {
        console.warn('error');
        console.warn(input);

    }

    @computed.struct get queryResult(): PotentialArray < ListResult<T> | undefined > {
        
        if ( this.queryListResult ) {
            let result: any = processPotentialArray(this.queryListResult, (input) => {
                return queryListResultConverter(input.data);
            });
            return result as any;
        }
        return undefined;
    }

    @computed get loading(): PotentialArray< boolean > {
        if ( this.queryListResult ) {
            return processPotentialArray(this.queryListResult, (input) => {
                return input.loading;
            });
        }
        return true;
    }

    @computed.struct get loadingError(): PotentialArray<any> {
        if (this.queryListResult) {
            return processPotentialArray(this.queryListResult, (input) => {
                return input.error;
            });
        }
        return undefined;
    }

    @computed.struct get getParsedItems(): PotentialArray<O[]> {
        const me = this;
        let queryResult = this.queryResult;
        if ( queryResult ) {
            let result = processPotentialArray(queryResult, (input) => {
                if (input) {
                    let items = input.items;
                    /* tslint:disable:no-string-literal */
                    /* tslint:enable:no-string-literal */
                    return items.map(me.toObject);
                }
                return undefined;
            });
            return result;
        } 
        return [];
    }

    @action loadMore(index?: number) {
        if (
            !this.loading
        ) {
            const me = this;
            // Only calls the function if:
            //  queryResult is not array
            //  queryResult contains given defined index
            // calls for every element if index is undefined
            processPotentialArray( 
                this.queryResult,
                (procQueryResult, procQueryResultInd) => {
                
                    // this checks if the current query is actually loading.
                    if ( 
                        Array.isArray( me.isLoadingMore ) &&
                        // this index should always be set is we are dealing with array.
                        !isNaN(procQueryResultInd) && 
                        // isLoadingMore is properly set according to index
                        procQueryResultInd > 0 &&
                        procQueryResultInd < me.isLoadingMore.length &&
                        // cancle the process if we isLoadingMore is already set.
                        me.isLoadingMore[procQueryResultInd]
                    ) {
                        return;
                    } else if ( me.isLoadingMore === true ) { 
                        // if isLoadingMore is not an array
                        // we want it to definitively be true
                        // to cancel the loading.
                        return;
                    } else if ( 
                        // check if there is nextToken to be loaded.
                        !(
                            procQueryResult &&
                            procQueryResult.nextToken &&
                            procQueryResult.nextToken.length &&
                            procQueryResult.nextToken.length > 0
                        )
                    ) {
                        return;
                    }

                    // set the isLoadingMore to be true
                    // so we don't load twice
                    if (!isNaN(procQueryResultInd)) {
                        me.isLoadingMore = me.isLoadingMore || [];
                        me.isLoadingMore[procQueryResultInd] = true;
                    } else { // index is undefined only if there is just one value
                        me.isLoadingMore = true;
                    }
                    
                    // set the baseVariables;
                    let variables = { nextToken: procQueryResult.nextToken };
                    const listVariables = reduce(me.listVariables);
                    // load specified index if array or will load the item itself.
                    processPotentialArray(
                        listVariables,
                        (variableInput, variableInputIndex) => {
                            if (
                                typeof (variableInput) !== 'undefined' && 
                                variableInputIndex === procQueryResultInd
                            ) {
                                variables = Object.assign(variables, variableInput);
                            }
                        }, 
                        procQueryResultInd
                    );
                    
                    // load specified index if array or will load the item itself
                    processPotentialArray(
                        me.queryListResult, 
                        (queryListResultInput) => {
                            if (typeof (queryListResultInput) !== 'undefined') {
                                queryListResultInput.ref.fetchMore({
                                    variables: { input: variables },
                                    query: me.listQueries,
                                    updateQuery: (previousResult, { fetchMoreResult }) => {
                                        const queryKey = getFirstKey(previousResult);
                                        
                                        if (queryKey !== false) {
                                            const previousItems = previousResult[queryKey].items ;
                                            const newItems = fetchMoreResult[queryKey].items ;
                                            const nextToken = fetchMoreResult[queryKey].nextToken;
                                            
                                            this.isLoadingMore = false;
                                            
                                            if (newItems && newItems.length) {
                                                return {
                                                    [queryKey]: {
                                                        nextToken: nextToken,
                                                        items: [
                                                            ...previousItems, 
                                                            ...newItems
                                                        ]
                                                    }
                                                };
                                            }
                                        }
                                        
                                        return previousResult;
                                    }
                                });
                            }
                        }, 
                        procQueryResultInd
                    );
                    
                }, 
                index
            );
        }
    }

    @action create(input: O) {
        if (this.createQueries) {
            this.operate({
                mutation: this.createQueries,
                variables: { input: this.convertInput(input) },
                update: this.cacheOperationConverter((cached, mutated) => {
                    cached.items.unshift(mutated);
                })
            });
        }
    }

    @action update(input: O) {
        if (this.updateQueries) {
            this.operate({
                mutation: this.updateQueries,
                variables: { input: this.convertInput(input) },
                update: this.cacheOperationConverter((cached, mutated) => {
                    const itemIndex = cached.items.findIndex(o => o.typedId === mutated.typedId);
                    if (
                        !isNaN(itemIndex) &&
                        itemIndex >= 0 &&
                        itemIndex < cached.items.length
                    ) {
                        cached.items.splice(itemIndex, 1, mutated);
                    }
                })
            });
        }
    }

    @action delete(_input: O) {
        if (this.deleteQueries) {
            let input = this.parseDelete(_input);
            this.operate({
                mutation: this.deleteQueries,
                variables: { input: input },
                update: this.cacheOperationConverter((cached, mutated)  => {
                    const itemIndex = cached.items.findIndex(o => o.typedId === mutated.typedId);
                    if (
                        !isNaN(itemIndex) &&
                        itemIndex >= 0 &&
                        itemIndex < cached.items.length
                    ) {
                        cached.items.splice(itemIndex, 1);
                    }
                })
            });
        }
    }

    @action updateQueryResult(newData: any, index?: number) {
        if (Array.isArray(this.lastQuery)) {
            if (
                typeof index  !== 'undefined' && 
                index >= 0 &&
                this.lastQuery.length >= 0 &&
                index < this.lastQuery.length
            ) {
                this.lastQuery[index].gqlObject.update(newData);
            }
        } else {
            this.lastQuery.gqlObject.update(newData);
        }
        
    }

    @action operate(options: MutationOptions<T>, validate: boolean = true) {
        if (validate) {
            let errors = this.invalidate(options.variables.input);
            if (errors) {
                this.operationErrors = errors;
            }
        }
        const me = this;
        
        apolloStore.runQueryWhenClientLoaded((client: ApolloClient<any>) => {
            client.mutate(options)
            .catch(error => { 
                console.error(error.message);
                me.operationErrors = error;
            });
        });
    }

    _subscribeToLastQuery(
        query: LastGQL<T>,
        _subscriptions: Reducable<SubscribeToMoreOptions[]>
    ) { 
        processPotentialArray(query, (lastQuery, currentIndex) => {
            let subscriptions = reduce(_subscriptions, lastQuery, currentIndex);
            subscriptions.forEach(subscription => {
                query.gqlObject.ref.subscribeToMore(subscription);
            });
        });
    }

    @action.bound forceUpdate() {
        this.lastUpdate = Date.now();
    }

    protected variableRemap(input: any) {
        const fetchPolicy = 'cache-and-network' as FetchPolicy;
        return {
            client: apolloStore.client,
            query: this.listQueries,
            onFetch: this.onFetch.bind(this),
            onError: this.onError.bind(this),
            variables: removeTypenameRecursive(input),
            fetchPolicy
        };
    }

    protected onLoad(r: any) {
        return;
    }

    protected refetchCode (mutationOptions: MutationOptions<T>) {
        const me = this;
        const listVariables = reduce(me.listVariables);
        if (Array.isArray (listVariables) ) {
            return listVariables.map(listVariable => ({ 
                query: me.listQueries,
                variables: listVariable
            }));
        } else {
            return [{
                query: me.listQueries,
                variables: listVariables
            }];
        }
    }

    protected parseUnitDelete(obj: O): UnitDeleteInput {
        let _obj = obj as any;
        return {
            unitId: _obj.unitId,
            typedId: _obj.typedId
        };
    }

    protected parseClientDelete(obj: O): ClientDeleteInput {
        let _obj = obj as any;
        return {
            clientId: _obj.clientId,
            typedId: _obj.typedId
        };
    }

    protected get lastQuery(): PotentialArray< LastGQL<T> > {
        if ( typeof (this._lastQuery) === 'undefined' ) {
            if (this.isPotentialArray) {
                this._lastQuery = [];
            } else {
                this._lastQuery = {
                    hash: '',
                    gqlObject: undefined,
                    variables: undefined
                };
            }
        } 
        // converting observable array to array;
        let lastQuery: any = this._lastQuery;
        if ( lastQuery.slice ) {
            return lastQuery.slice();
        } else {
            return lastQuery;
        }
    }

    protected cacheOperationConverter(cacheOperation: CacheOperation<T, O>) {
        const me = this;
        
        return (cache, next) => {
            let { data, subscriptionData } = next;
            if (!data) {
                if ( subscriptionData && subscriptionData.data ) {
                    data = subscriptionData;
                } else {
                    return;
                }
            }
            const queryResultKey = getFirstKey(data);         
            if ( 
                !queryResultKey ||
                Array.isArray(me.queryListResult)
            ) {
                return;
            }

            const queryResultValue = data[queryResultKey];

            const reducedVariable = reduce(this.listVariables, );
            const previousData = apolloStore.client.readQuery({
                query: me.listQueries,
                variables: reducedVariable
            });

            Object.keys(previousData).forEach(key => {
                if ( key.includes('list') ) {
                    cacheOperation(previousData[key], queryResultValue);
                }
            });
            let finalResult = {
                query: me.listQueries,
                variables: reducedVariable,
                data: previousData
            };
            
            apolloStore.client.writeQuery(finalResult);
            (me.lastQuery as LastGQL<T>).gqlObject.update(previousData);
            me.forceUpdate.apply(me);
        };
    }    

    protected subscriptionToUpdate(args: any) {
        subscriptionToUpdate(args);
    }

    protected updateTypedItem(typeKey: string, queryKey: string, getFilter?: any) {
        const updateProcess = (data, {gqlObject}) => {
            if (
                gqlObject &&
                gqlObject.data &&
                gqlObject.data[queryKey] &&
                gqlObject.data[queryKey].items
            ) {
                const { data : { updateSubscribers }} = data;
                const actualData = updateSubscribers[typeKey];

                const gqlObjectData = gqlObject.data;
                const currentItems = gqlObjectData[queryKey].items;
                let mutated = false;

                const updatedItems = currentItems.map(item => {
                    const indexFilter = typeof getFilter === 'undefined' ? 
                        o => o.typedId === item.typedId : getFilter(item);
                    let dataIndex = actualData.findIndex(indexFilter);
                    if (
                        dataIndex >= 0 ||
                        dataIndex < actualData .length
                    ) {
                        mutated = true;
                        return Object.assign({}, item, actualData [dataIndex]);
                    }
                    return item;
                });

                actualData.forEach(o => {
                    if ( updatedItems.every(
                        updatedItem => updatedItem.typedId !== o.typedId &&
                        updatedItem.unitId === o.unitId
                    )) {
                        mutated = true;
                        updatedItems.push(o);
                    }
                });

                if (mutated) {
                    gqlObject.update({
                        ...gqlObjectData, 
                        [queryKey]: {
                            ...gqlObjectData[queryKey],
                            items : updatedItems 
                        } 
                    });
                }
            }
        };
        return {
            next: (data) => {
                if (Array.isArray(this.lastQuery)) {
                    processArrayAsync(this.lastQuery, updateProcess.bind(this, data));
                } else {
                    updateProcess(data, this.lastQuery);
                }
                // this.updateQueryResult(data);
            }
        };
    }

    private get isPotentialArray (): boolean {
        return Array.isArray(reduce(this.listVariables));
    }

    private convertInput(_input: any): any {
        let input;
        if (_input.parseAbsolute) {
            input = _input.parseAbsolute();
        } else {
            input = this.toObject(_input).parseAbsolute();
        }
        return removeTypenameRecursive(input);
    }
    
    private getGQLObjectFromVariable(variable: any, onFirstLoad?: any) {
        return  gql<QueryListResult<T>>(Object.assign({}, this.variableRemap(variable), { onFirstLoad }));
    }

    private getQuery(newVariable: any, index?: number) {
        let lastQuery = this.lastQuery;
        let newHash = JSON.stringify(newVariable);
        let gqlObject;
        if ( 
            Array.isArray(lastQuery)
        ) {
            if (isNaN(index)) {
                throw new Error('index must be provided if lastQuery is suppose to be array.');
            }
            if (
                lastQuery.length > index &&
                index > 0
            ) {
                if (lastQuery[index]) {
                    if (
                        lastQuery[index].hash &&
                        lastQuery[index].hash === newHash 
                    ) {
                        return lastQuery[index].gqlObject;
                    } else {
                        this.destroyGQL(lastQuery[index]);
                    } 
                } 
            }    
            gqlObject = this.getGQLObjectFromVariable(newVariable);
            
            lastQuery[index] = {
                hash: newHash,
                gqlObject,
                variables: newVariable
            };
            this._lastQuery = lastQuery;

            this._subscribeToLastQuery(lastQuery[index], this._allSubscriptions);
        } else {
            if (lastQuery.hash === newHash) {
                return lastQuery.gqlObject;
            } else {
                gqlObject = this.getGQLObjectFromVariable(newVariable);

                if (lastQuery.gqlObject) {
                    this.destroyGQL(lastQuery);
                }

                this._lastQuery = {
                    hash: newHash,
                    gqlObject,
                    variables: newVariable
                };

                this._subscribeToLastQuery(this._lastQuery, this._allSubscriptions);
            }
        }
        return gqlObject;
    }

   private loadCall(r: any) {
        if (
            !this.manualLoad &&
            this.queryResult
        ) {
            this.loadMore();
        }
        this.onLoad(r);
    }

    private invalidate(obj: T) {
        let errors = this.countError(obj);
        if (errors.length === 0) {
            return false;
        } else {
            return errors;
        }

    }

    private destroyGQL(input: LastGQL<T>) {
        // input.gqlObject.ref.unsubscribe();
        delete input.gqlObject;
    }

}


// WEBPACK FOOTER //
// ./src/stores/application/generic/datastore.ts